import Swiper from 'swiper';
import { gsap } from 'gsap';
import {
	Autoplay,
	Controller,
	EffectFade,
	Navigation,
	Pagination,
} from 'swiper/modules';

let heroSliderElements = null;
let heroAnimationTL = null;
let heroXlAnimationTL = null;
let isXlScreen = window.innerWidth > 1500;

let resizeTimeout;
const handleResize = () => {
	clearTimeout(resizeTimeout);
	resizeTimeout = setTimeout(() => {
		isXlScreen = window.innerWidth > 1500;
	}, 150);
};

window.addEventListener('resize', handleResize, { passive: true });

// Cache DOM elements for better performance
const cacheHeroElements = () => {
	if (heroSliderElements) return heroSliderElements;

	heroSliderElements = {
		allSlides: document.querySelectorAll('.hero-slider .swiper-slide'),
		sliderMask: document.querySelector('#slide-clip'),
		sliderMaskXl: document.querySelector('#slide-clip-xl'),
		purpleRectangle: document.querySelector('.purple-rectangle'),
		yellowCircle: document.querySelector('.yellow-circle'),
		purpleRectangleXl: document.querySelector('.purple-rectangle-xl'),
		yellowCircleXl: document.querySelector('.yellow-circle-xl'),
		customPaginationElements:
			document.querySelectorAll('.custom-pagination'),
	};

	heroSliderElements.slideElements = Array.from(
		heroSliderElements.allSlides
	).map((slide) => ({
		slide,
		content: slide.querySelector('.slide-text-content'),
		heading: slide.querySelector('.slide-text-content h1'),
		description: slide.querySelector('.slide-text-content p'),
		button: slide.querySelector('.slide-text-content .button-container'),
		image: slide.querySelector('.slide-image'),
	}));

	if (heroSliderElements.sliderMask) {
		heroSliderElements.maskElements = {
			circle: heroSliderElements.sliderMask.querySelector('#circle'),
			square1: heroSliderElements.sliderMask.querySelector('#square1'),
			square2: heroSliderElements.sliderMask.querySelector('#square2'),
		};
	}

	if (heroSliderElements.sliderMaskXl) {
		heroSliderElements.maskElementsXl = {
			circle: heroSliderElements.sliderMaskXl.querySelector('#circle'),
			square1: heroSliderElements.sliderMaskXl.querySelector('#square1'),
			square2: heroSliderElements.sliderMaskXl.querySelector('#square2'),
		};
	}

	return heroSliderElements;
};

const heroSlideTextAnimate = (activeIndex, isTransitionStart = false) => {
	const elements = cacheHeroElements();

	if (isTransitionStart) {
		const exitTL = gsap.timeline();

		elements.slideElements.forEach((slideElement) => {
			const { heading, description, button, image } = slideElement;

			if (heading) {
				exitTL.to(
					heading,
					{
						y: 30,
						opacity: 0,
						duration: 0.6,
						ease: 'accelerateEasing',
						force3D: true,
						willChange: 'transform, opacity',
					},
					0
				);
			}

			if (description) {
				exitTL.to(
					description,
					{
						y: 30,
						opacity: 0,
						duration: 0.6,
						delay: 0.2,
						ease: 'accelerateEasing',
						force3D: true,
						willChange: 'transform, opacity',
					},
					0
				);
			}

			if (button) {
				exitTL.to(
					button,
					{
						y: 30,
						opacity: 0,
						duration: 0.6,
						delay: 0.4,
						ease: 'accelerateEasing',
						force3D: true,
						willChange: 'transform, opacity',
					},
					0
				);
			}

			if (image) {
				exitTL.to(
					image,
					{
						opacity: 0,
						duration: 0.4,
						ease: 'accelerateEasing',
						willChange: 'opacity',
					},
					0
				);
			}
		});

		// Clean up willChange after animation
		exitTL.call(() => {
			elements.slideElements.forEach((slideElement) => {
				const { heading, description, button, image } = slideElement;
				const elementsToClean = [
					heading,
					description,
					button,
					image,
				].filter(Boolean);
				gsap.set(elementsToClean, { willChange: 'auto' });
			});
		});
	} else {
		if (elements.slideElements[activeIndex]) {
			const { heading, description, button, image } =
				elements.slideElements[activeIndex];

			const entranceTL = gsap.timeline();

			if (heading) {
				gsap.set(heading, {
					y: 30,
					opacity: 0,
					force3D: true,
					willChange: 'transform, opacity',
				});
				entranceTL.to(heading, {
					y: 0,
					opacity: 1,
					duration: 0.74,
					delay: 1,
					ease: 'buttonEasing',
					force3D: true,
				});
			}

			if (description) {
				gsap.set(description, {
					y: 30,
					opacity: 0,
					force3D: true,
					willChange: 'transform, opacity',
				});
				entranceTL.to(
					description,
					{
						y: 0,
						opacity: 1,
						duration: 0.5,
						delay: 1.3,
						ease: 'buttonEasing',
						force3D: true,
					},
					0
				);
			}

			if (button) {
				gsap.set(button, {
					y: 30,
					opacity: 0,
					force3D: true,
					willChange: 'transform, opacity',
				});
				entranceTL.to(
					button,
					{
						y: 0,
						opacity: 1,
						duration: 0.5,
						delay: 1.5,
						ease: 'buttonEasing',
						force3D: true,
					},
					0
				);
			}

			if (image) {
				gsap.set(image, { willChange: 'opacity' });
				entranceTL.to(
					image,
					{
						opacity: 1,
						duration: 0.4,
						ease: 'buttonEasing',
					},
					0
				);
			}

			// Clean up willChange after animation
			entranceTL.call(() => {
				const elementsToClean = [
					heading,
					description,
					button,
					image,
				].filter(Boolean);
				gsap.set(elementsToClean, { willChange: 'auto' });
			});
		}
	}
};

const heroSliderAnimate = (slideIndex) => {
	const elements = cacheHeroElements();

	if (
		!elements.sliderMask ||
		!elements.maskElements.circle ||
		!elements.maskElements.square1 ||
		!elements.maskElements.square2
	)
		return;

	const { circle, square1, square2 } = elements.maskElements;
	const { purpleRectangle, yellowCircle } = elements;

	// Kill any existing animations and create new timeline
	if (heroAnimationTL) heroAnimationTL.kill();
	heroAnimationTL = gsap.timeline();

	// Set transform origin and will-change for better performance
	const animatedElements = [circle, square1, square2];
	if (purpleRectangle) animatedElements.push(purpleRectangle);
	if (yellowCircle) animatedElements.push(yellowCircle);

	gsap.set([circle, square1, square2], {
		transformOrigin: '50% 50%',
		force3D: true,
		willChange: 'transform',
		backfaceVisibility: 'hidden',
		perspective: 1000,
		transformStyle: 'preserve-3d',
	});

	if (purpleRectangle) {
		gsap.set(purpleRectangle, {
			transformOrigin: '50% 50%',
			force3D: true,
			willChange: 'transform',
		});
	}

	if (yellowCircle) {
		gsap.set(yellowCircle, {
			transformOrigin: '50% 50%',
			force3D: true,
			willChange: 'transform',
		});
	}

	const animationCycle = (slideIndex - 1) % 3; // Cycle through 3 states: 0, 1, 2

	if (animationCycle === 0) {
		heroAnimationTL
			.to(
				circle,
				{
					x: 365,
					y: 59,
					duration: 1.6,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square1,
				{
					x: -101,
					y: -83,
					rotate: -48,
					scale: 1.1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square2,
				{
					x: -26,
					y: -29,
					duration: 1.3,
					rotate: -98,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			);

		if (purpleRectangle) {
			heroAnimationTL.to(
				purpleRectangle,
				{
					x: 110,
					y: -238,
					rotate: 93,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}

		if (yellowCircle) {
			heroAnimationTL.to(
				yellowCircle,
				{
					x: -256,
					y: -68,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}
	} else if (animationCycle === 1) {
		// Second animation
		heroAnimationTL
			.to(
				circle,
				{
					x: 395,
					y: 34,
					scale: 1.3,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square2,
				{
					x: -126,
					y: 57,
					rotate: -30,
					scale: 1.15,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square1,
				{
					y: -186 * 0.72,
					x: -50 * 0.72,
					rotate: 61,
					scale: 1.4,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			);

		if (purpleRectangle) {
			heroAnimationTL.to(
				purpleRectangle,
				{
					x: 0,
					y: -402 * 0.72,
					rotate: 45,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}

		if (yellowCircle) {
			heroAnimationTL.to(
				yellowCircle,
				{
					x: -413 * 0.72,
					y: -151 * 0.72,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}
	} else {
		// Third animation (reset to starting position)
		heroAnimationTL
			.to(
				circle,
				{
					x: 0,
					y: 0,
					scale: 1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square1,
				{
					x: 0,
					y: 0,
					rotate: -10,
					scale: 1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square2,
				{
					rotate: 10,
					x: 0,
					y: 0,
					scale: 1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			);

		if (purpleRectangle) {
			heroAnimationTL.to(
				purpleRectangle,
				{
					x: 0,
					y: 0,
					rotate: 0,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}

		if (yellowCircle) {
			heroAnimationTL.to(
				yellowCircle,
				{
					x: 0,
					y: 0,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}
	}

	// Clean up willChange after animation completes for better memory management
	heroAnimationTL.call(() => {
		gsap.set(animatedElements, { willChange: 'auto' });
	});
};

const heroSliderAnimateXl = (slideIndex) => {
	const elements = cacheHeroElements();

	if (
		!elements.sliderMaskXl ||
		!elements.maskElementsXl?.circle ||
		!elements.maskElementsXl?.square1 ||
		!elements.maskElementsXl?.square2
	)
		return;

	const { circle, square1, square2 } = elements.maskElementsXl;
	const { purpleRectangleXl, yellowCircleXl } = elements;

	if (heroXlAnimationTL) heroXlAnimationTL.kill();
	heroXlAnimationTL = gsap.timeline();

	// Set transform origin and will-change for better performance
	const animatedElements = [circle, square1, square2];
	if (purpleRectangleXl) animatedElements.push(purpleRectangleXl);
	if (yellowCircleXl) animatedElements.push(yellowCircleXl);

	// Special handling for SVG mask elements - Safari optimization
	gsap.set([circle, square1, square2], {
		transformOrigin: '50% 50%',
		force3D: true,
		willChange: 'transform',
		backfaceVisibility: 'hidden',
		perspective: 1000,
		transformStyle: 'preserve-3d',
	});

	// Regular elements (non-SVG)
	if (purpleRectangleXl) {
		gsap.set(purpleRectangleXl, {
			transformOrigin: '50% 50%',
			force3D: true,
			willChange: 'transform',
		});
	}

	if (yellowCircleXl) {
		gsap.set(yellowCircleXl, {
			transformOrigin: '50% 50%',
			force3D: true,
			willChange: 'transform',
		});
	}

	// Animation cycle based on slide index (adjusted for 1-based indexing)
	const animationCycle = (slideIndex - 1) % 3; // Cycle through 3 states: 0, 1, 2

	if (animationCycle === 0) {
		// First animation - batch all animations in timeline
		heroXlAnimationTL
			.to(
				circle,
				{
					x: 508,
					y: 83,
					duration: 1.6,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square1,
				{
					x: -141,
					y: -116,
					rotate: -48,
					scale: 1.1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square2,
				{
					x: -37,
					y: -41,
					duration: 1.3,
					rotate: -78,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			);

		if (purpleRectangleXl) {
			heroXlAnimationTL.to(
				purpleRectangleXl,
				{
					x: 153,
					y: -331,
					rotate: 93,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}

		if (yellowCircleXl) {
			heroXlAnimationTL.to(
				yellowCircleXl,
				{
					x: -356,
					y: -95,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}
	} else if (animationCycle === 1) {
		// Second animation
		heroXlAnimationTL
			.to(
				circle,
				{
					x: 549,
					y: 48,
					scale: 1.3,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square2,
				{
					x: -175,
					y: 80,
					rotate: -30,
					scale: 1.15,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square1,
				{
					y: -186,
					x: -50,
					rotate: 61,
					scale: 1.4,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			);

		if (purpleRectangleXl) {
			heroXlAnimationTL.to(
				purpleRectangleXl,
				{
					x: 0,
					y: -402,
					rotate: 45,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}

		if (yellowCircleXl) {
			heroXlAnimationTL.to(
				yellowCircleXl,
				{
					x: -413,
					y: -151,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}
	} else {
		// Third animation (reset to starting position)
		heroXlAnimationTL
			.to(
				circle,
				{
					x: 0,
					y: 0,
					scale: 1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square1,
				{
					x: 0,
					y: 0,
					rotate: -10,
					scale: 1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			)
			.to(
				square2,
				{
					rotate: 10,
					x: 0,
					y: 0,
					scale: 1,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
					rotation: 0.01, // Force layer creation for Safari
				},
				0
			);

		if (purpleRectangleXl) {
			heroXlAnimationTL.to(
				purpleRectangleXl,
				{
					x: 0,
					y: 0,
					rotate: 0,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}

		if (yellowCircleXl) {
			heroXlAnimationTL.to(
				yellowCircleXl,
				{
					x: 0,
					y: 0,
					duration: 1.3,
					ease: 'power2.inOut',
					force3D: true,
					willChange: 'transform',
				},
				0
			);
		}
	}

	// Clean up willChange after animation completes for better memory management
	heroXlAnimationTL.call(() => {
		gsap.set(animatedElements, { willChange: 'auto' });
	});
};

const initSwiper = () => {
	cacheHeroElements();

	const mainHeroSlider = new Swiper('.hero-slider', {
		slidesPerView: 1,
		navigation: {
			nextEl: '.hero-slider-next',
			prevEl: '.hero-slider-prev',
		},
		speed: 1000,
		effect: 'fade',
		fadeEffect: {
			crossFade: true,
		},
		pagination: {
			el: '.hero-slider-pagination-top',
			clickable: true,
		},
		autoplay: {
			delay: 4000,
			disableOnInteraction: false,
		},
		on: {
			slideChangeTransitionStart: function () {
				heroSlideTextAnimate(null, true);
			},
			slideChange: function () {
				// Use RAF for better performance
				requestAnimationFrame(() => {
					const activeIndex = this.activeIndex;
					const elements = cacheHeroElements();

					// Handle custom pagination more efficiently
					elements.customPaginationElements.forEach(
						(paginationElement) => {
							const bullets = paginationElement.querySelectorAll(
								'.swiper-pagination-bullet'
							);

							// Use a document fragment for batch DOM updates
							bullets.forEach((bullet, index) => {
								bullet.classList.toggle(
									'swiper-pagination-bullet-active',
									index === activeIndex
								);
							});
						}
					);

					// Throttle animations to prevent overlap and reduce Safari lag
					if (heroAnimationTL && heroAnimationTL.isActive()) {
						heroAnimationTL.kill();
					}
					if (heroXlAnimationTL && heroXlAnimationTL.isActive()) {
						heroXlAnimationTL.kill();
					}

					heroSlideTextAnimate(activeIndex);
					if (isXlScreen) {
						heroSliderAnimateXl(activeIndex);
					} else {
						heroSliderAnimate(activeIndex);
					}
				});
			},
			init: function () {
				const activeIndex = this.activeIndex;
				const elements = cacheHeroElements();

				elements.customPaginationElements.forEach(
					(paginationElement) => {
						const bullets = paginationElement.querySelectorAll(
							'.swiper-pagination-bullet'
						);

						bullets.forEach((bullet, index) => {
							bullet.classList.toggle(
								'swiper-pagination-bullet-active',
								index === activeIndex
							);

							bullet.addEventListener(
								'click',
								() => {
									mainHeroSlider.slideTo(index);
								},
								{ passive: true }
							);
						});
					}
				);
			},
		},
		modules: [Navigation, Pagination, Autoplay, Controller, EffectFade],
	});

	new Swiper('.testimonials-slider', {
		speed: 800,
		autoplay: {
			delay: 15000,
		},
		slidesPerView: 1,
		navigation: {
			nextEl: '.testimonials-slider-next',
			prevEl: '.testimonials-slider-prev',
		},
		pagination: {
			el: '.testimonials-slider-pagination',
			clickable: true,
		},
		autoHeight: true,
		modules: [Navigation, Pagination, Autoplay],
	});

	new Swiper('.news-slider', {
		slidesPerView: 1.15,
		spaceBetween: 16,
		slidesOffsetBefore: 20,
		breakpoints: {
			1024: {
				slidesPerView: 3,
				spaceBetween: 40,
				slidesOffsetBefore: 0,
			},
		},
	});

	new Swiper('.events-slider', {
		slidesPerView: 1,
		breakpoints: {
			1024: {
				slidesPerView: 2,
				spaceBetween: 40,
			},
		},
	});

	new Swiper('.tab-slider', {
		slidesPerView: 'auto',
		slidesOffsetBefore: 20,
		breakpoints: {
			1024: {
				spaceBetween: 2,
				slidesOffsetBefore: 0,
			},
		},
	});

	new Swiper('.eureka-slider-mobile', {
		slidesPerView: 1,
		pagination: {
			el: '.eureka-slider-pagination',
			clickable: true,
		},
		speed: 800,
		modules: [Pagination, Controller],
	});

	const bottomSlider = new Swiper('.eureka-slider-bottom', {
		slidesPerView: 1,
		pagination: {
			el: '.eureka-slider-pagination',
			clickable: true,
		},
		speed: 800,

		modules: [Navigation, Pagination, Controller],
	});

	const imageSwiper = new Swiper('.eureka-slider-image', {
		slidesPerView: 1,
		navigation: {
			prevEl: '.eureka-slider-prev',
			nextEl: '.eureka-slider-next',
		},
		speed: 800,

		modules: [Navigation, Pagination, Controller],
	});

	bottomSlider.controller.control = imageSwiper;
	imageSwiper.controller.control = bottomSlider;

	new Swiper('.departments-slider', {
		slidesPerView: 1.15,
		slidesOffsetBefore: 20,
		spaceBetween: 16,
		breakpoints: {
			1024: {
				slidesOffsetBefore: 0,
			},
		},
	});

	new Swiper('.single-product-slider', {
		slidesPerView: 1,
		spaceBetween: 0,
		navigation: {
			nextEl: '.single-product-slider-next',
			prevEl: '.single-product-slider-prev',
		},
		pagination: {
			el: '.single-product-slider-pagination',
			clickable: true,
		},

		modules: [Navigation, Pagination],
	});

	new Swiper('.single_product_crossell-slider', {
		slidesPerView: 1.6,
		spaceBetween: 16,
	});

	new Swiper('.content-slider', {
		slidesPerView: 1,
		navigation: {
			nextEl: '.content-slider-next',
			prevEl: '.content-slider-prev',
		},
		pagination: {
			el: '.content-slider-pagination',
			clickable: true,
		},
		modules: [Navigation, Pagination],
	});

	new Swiper('.school-facilities-slider', {
		slidesPerView: 1,
		navigation: {
			nextEl: '.single-product-slider-next',
			prevEl: '.single-product-slider-prev',
		},
		pagination: {
			el: '.swiper-pagination',
			clickable: true,
		},
		loop: true,
		modules: [Navigation, Pagination],
	});

	new Swiper('.modal-gallery-slider', {
		slidesPerView: 1,
		loop: true,
		pagination: {
			el: '.modal-gallery-pagination',
			clickable: true,
		},
		navigation: {
			nextEl: '.modal-gallery-next',
			prevEl: '.modal-gallery-prev',
		},
		modules: [Navigation, Pagination],
	});

	new Swiper('.marquee-left', {
		slidesPerView: 'auto',
		spaceBetween: 8,
		loop: true,
		speed: 6000,
		allowTouchMove: false,
		autoplay: {
			delay: 1,
			disableOnInteraction: false,
		},
		modules: [Autoplay],
	});

	new Swiper('.marquee-right', {
		slidesPerView: 'auto',
		spaceBetween: 8,
		loop: true,
		speed: 6000,
		allowTouchMove: false,
		reverseDirection: true,
		autoplay: {
			delay: 1,
			disableOnInteraction: false,
			reverseDirection: true,
		},
		modules: [Autoplay],
	});
};

// Cleanup function for better memory management
const cleanupHeroAnimations = () => {
	if (heroAnimationTL) {
		heroAnimationTL.kill();
		heroAnimationTL = null;
	}
	if (heroXlAnimationTL) {
		heroXlAnimationTL.kill();
		heroXlAnimationTL = null;
	}
	heroSliderElements = null;

	// Remove resize listener
	window.removeEventListener('resize', handleResize);
	clearTimeout(resizeTimeout);
};

export { initSwiper, cleanupHeroAnimations };
